import { db } from "../../../config/firebase/firebase-config";
import {
  setEntity,
  setFileParameterization,
  setParamResponseTime,
} from "../adapter/EntityAdapter";
import { setEntityFormToUser } from "../../admin/adapter/UserAdapter";
import { EntityForm } from "../form/EntityFrom";
import {WorkScheduleFormValues} from "../form/WorkScheduleFormValues";
import {setWorkScheduleParam} from "../adapter/WorkScheduleAdapter";
import {IWorkScheduleDoc} from "../doc/IWorkScheduleDoc";
import {ISpecialDayDoc} from "../doc/ISpecialDayDoc";
import {SpecialDay} from "../form/SpecialDay";
import {specialDayAdapter} from "../adapter/SpecialDayAdapter";

export const createEntity = async (entity: EntityForm) => {
  let userData = setEntityFormToUser(entity, entity.uid);
  await db
    .collection("Users")
    .doc(entity.nit)
    .set(
      {
        ...userData,
      },
      { merge: true }
    );

  // entity is gotten
  let entityData = setEntity(entity, entity.uid);
  await db
    .collection("Entities")
    .doc(entity.nit)
    .set(
      {
        ...entityData,
      },
      { merge: true }
    );

  // ParamResponseTimeData is gotten
  const docRef = db.collection("Correspondence").doc(entity.nit);
  let ParamResponseTimeData = setParamResponseTime();
  await docRef.set({ idEntity: entity.nit }).then(() => {
    return docRef
      .collection("ParamResponseTime")
      .doc(entity.nit)
      .set(
        {
          ...ParamResponseTimeData,
        },
        { merge: true }
      );
  });

  //file
  const docRefile = db.collection("FileModule").doc(entity.nit);

  await docRefile.set({ idEntity: entity.nit }).then(() => {
    return docRefile
      .collection("Parameterization")
      .doc(entity.nit)
      .set({ ...setFileParameterization() }, { merge: true });
  });

  return {
    ok: true,
  };
};

export const updateEntity = async (entity: EntityForm): Promise<any> => {
  let userData = setEntityFormToUser(entity, entity.uid);
  await db
    .collection("Users")
    .doc(entity.nit)
    .set(
      {
        ...userData,
      },
      { merge: true }
    );

  // entity is gotten
  let entityData = setEntity(entity, entity.uid);
  await db
    .collection("Entities")
    .doc(entity.nit)
    .set(
      {
        ...entityData,
      },
      { merge: true }
    );

  return {
    ok: true,
  };
};

export const EntityById = async (idEntity: any) => {
  const snap = await db
    .collection("Entities")
    .where("idEntity", "==", idEntity)
    .limit(1)
    .get();
  if (snap.empty) {
    return null;
  }
  let response: any;

  snap.forEach((doc: any) => {
    response = doc.data() as any;
  });

  return response;
};


/**
 * Guarda la configuración del horario laboral para una entidad específica en Firestore.
 *
 * Utiliza el identificador de la entidad y los valores del formulario para generar
 * un documento que será almacenado en la ruta:
 * Entities/{idEntity}/WorkSchedule/{idEntity}.
 *
 * Los datos se transforman usando la función `setWorkScheduleParam` antes de ser persistidos.
 *
 * @param idEntity - Identificador único de la entidad.
 * @param form - Datos del formulario de horario laboral.
 * @returns Promesa que resuelve a `{ ok: boolean }`, indicando si la operación fue exitosa.
 */
export const saveWorkSchedule = async (
  idEntity: string,
  form: WorkScheduleFormValues
): Promise<{ ok: boolean }> => {
  try {
    const workScheduleData: IWorkScheduleDoc = setWorkScheduleParam(form);

    const ref = db
      .collection("Entities")
      .doc(idEntity)
      .collection("WorkSchedule")
      .doc(idEntity);

    await ref.set(workScheduleData);

    return { ok: true };
  } catch (error) {
    return { ok: false };
  }
};

/**
 * Carga la configuración del horario laboral para una entidad desde Firestore.
 *
 * Consulta el documento en la ruta:
 * Entities/{entityId}/WorkSchedule/{entityId}, y retorna los datos
 * tipados como `IWorkScheduleDoc` si existen.
 *
 * @param entityId - Identificador único de la entidad.
 * @returns Promesa que resuelve con los datos del horario laboral o `null` si no existen o ocurre un error.
 */
export const loadWorkSchedule = async (
  entityId: string
): Promise<IWorkScheduleDoc | null> => {
  try {
    const docRef = await db
      .collection("Entities")
      .doc(entityId)
      .collection("WorkSchedule")
      .doc(entityId)
      .get();

    if (docRef.exists) {
      return docRef.data() as IWorkScheduleDoc;
    } else {
      console.error("📭 No existe horario laboral para esta entidad.");
    }
    return null;
  } catch (error) {
    return null;
  }
};

/**
 * Crea un nuevo día especial en Firestore para la entidad especificada.
 *
 * El documento se almacena en la ruta:
 * Entities/{idEntity}/SpecialDays/{id}
 *
 * Los datos se transforman utilizando `specialDayAdapter.toFirestore` antes de ser guardados.
 *
 * @param idEntity - Identificador único de la entidad.
 * @param day - Objeto `SpecialDay` que contiene la información del día a crear.
 * @returns Promesa que resuelve a `{ ok: boolean }` indicando si la operación fue exitosa.
 */
export const createSpecialDay = async (
  idEntity: string,
  day: SpecialDay
): Promise<{ ok: boolean }> => {
  try {
    const ref = db
      .collection("Entities")
      .doc(idEntity)
      .collection("SpecialDays")
      .doc(day.id);

    await ref.set(specialDayAdapter.toFirestore(day));
    return { ok: true };
  } catch (error) {
    console.error("❌ Error al crear día especial:", error);
    return { ok: false };
  }
};

/**
 * Obtiene todos los días especiales registrados para una entidad específica desde Firestore.
 *
 * Consulta todos los documentos en la ruta:
 * Entities/{idEntity}/SpecialDays
 *
 * Cada documento se transforma con `specialDayAdapter.fromFirestore`.
 *
 * @param idEntity - Identificador único de la entidad.
 * @returns Promesa que resuelve con un arreglo de `SpecialDay` o vacío si ocurre un error.
 */
export const getAllSpecialDays = async (
  idEntity: string
): Promise<SpecialDay[]> => {
  try {
    const ref = db
      .collection("Entities")
      .doc(idEntity)
      .collection("SpecialDays");

    const snapshot = await ref.get();
    const result: SpecialDay[] = [];

    snapshot.forEach((doc) => {
      result.push(specialDayAdapter.fromFirestore(doc.id, doc.data() as ISpecialDayDoc));
    });

    return result;
  } catch (error) {
    console.error("❌ Error al cargar días especiales:", error);
    return [];
  }
};

/**
 * Elimina un día especial de Firestore para una entidad específica.
 *
 * El documento a eliminar está en la ruta:
 * Entities/{idEntity}/SpecialDays/{id}
 *
 * @param idEntity - Identificador único de la entidad.
 * @param id - ID del documento que representa el día especial a eliminar.
 * @returns Promesa que resuelve a `{ ok: boolean }` indicando si la eliminación fue exitosa.
 */
export const deleteSpecialDay = async (
  idEntity: string,
  id: string
): Promise<{ ok: boolean }> => {
  try {
    const ref = db
      .collection("Entities")
      .doc(idEntity)
      .collection("SpecialDays")
      .doc(id);

    await ref.delete();
    return { ok: true };
  } catch (error) {
    console.error("❌ Error al eliminar día especial:", error);
    return { ok: false };
  }
};